import { FAB_SIZE_PX } from './fabPosition'

/**
 * Gini Tanıtım videosu sonrası logo uçuşunun saf yol hesabı (spec §12.4). DOM'a dokunmaz;
 * çıktı doğrudan `Element.animate()`'e verilir.
 *
 * Uçan öğe FAB ile aynı 56 px kutudur (`left: 0; top: 0`, `transform-origin: center`); büyüklük
 * `scale` ile verilir. Böylece ışıltı (FabGlow) logoyla orantılı büyür ve son kare FAB'in kendisiyle
 * piksel piksel aynıdır.
 */

export const FLIGHT_DURATION_MS = 1400
/** `prefers-reduced-motion`: uçuş yerine kısa solma. */
export const FLIGHT_FADE_MS = 300
/** Ekran ortasında beliren logonun çapı. */
export const FLIGHT_START_SIZE_PX = 160
/** Logo bu ana kadar ekran ortasında belirir ve bekler; sonra uçuşa geçer. */
export const FLIGHT_HOLD_OFFSET = 0.22
/** Belirme karesinin zamanı (0'dan buraya saydamlık 0 → 1). */
const FLIGHT_APPEAR_OFFSET = 0.12
/** Eğrinin kirişe dik sapması, kiriş boyunun oranı olarak. */
const FLIGHT_BOW = 0.35
/** Uçuş bölümündeki örnek kare sayısı. */
const FLIGHT_STEPS = 12

/** Logonun merkezi (viewport px) ve çapı. */
export interface FlightPoint {
  x: number
  y: number
  size: number
}

/** Tip takma adı (interface değil): `Keyframe`'in dizin imzasına doğrudan atanabilsin. */
export type FlightKeyframe = {
  offset: number
  opacity: number
  transform: string
}

const round = (value: number, digits = 2) => {
  const factor = 10 ** digits
  return Math.round(value * factor) / factor
}

function frame(offset: number, point: FlightPoint, opacity: number, baseSize: number): FlightKeyframe {
  const half = baseSize / 2
  const x = round(point.x - half)
  const y = round(point.y - half)
  // Ölçek 4 hane: 160 / 56 gibi oranlar iki hanede gözle görülür kayar.
  const scale = round(point.size / baseSize, 4)
  return { offset: round(offset, 4), opacity, transform: `translate(${x}px, ${y}px) scale(${scale})` }
}

/**
 * Kontrol noktası: kirişin ortasından, kirişe dik ve YUKARI doğru (ekranda y küçülen yön) sapar.
 * Kiriş yataysa bile yukarı, dikeyse sağa kavis yapar; hedef merkezle çakışırsa sapma yoktur.
 */
function controlPoint(from: FlightPoint, to: FlightPoint): { x: number; y: number } {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const length = Math.hypot(dx, dy)
  const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 }
  if (length < 1) return mid
  let nx = dy / length
  let ny = -dx / length
  if (ny > 0 || (ny === 0 && nx < 0)) {
    nx = -nx
    ny = -ny
  }
  return { x: mid.x + nx * FLIGHT_BOW * length, y: mid.y + ny * FLIGHT_BOW * length }
}

/**
 * Ekran ortasından (`from`) FAB'in güncel konumuna (`to`) uçuş kareleri: logo ortada belirir,
 * kısa bir an bekler, ardından ikinci dereceden Bézier eğrisiyle küçülerek hedefe iner.
 */
export function flightKeyframes(from: FlightPoint, to: FlightPoint, baseSize: number = FAB_SIZE_PX): FlightKeyframe[] {
  const control = controlPoint(from, to)
  const frames: FlightKeyframe[] = [
    frame(0, { ...from, size: from.size * 0.7 }, 0, baseSize),
    frame(FLIGHT_APPEAR_OFFSET, from, 1, baseSize),
    frame(FLIGHT_HOLD_OFFSET, from, 1, baseSize),
  ]

  for (let i = 1; i <= FLIGHT_STEPS; i++) {
    const t = i / FLIGHT_STEPS
    const u = 1 - t
    const point: FlightPoint = {
      x: u * u * from.x + 2 * u * t * control.x + t * t * to.x,
      y: u * u * from.y + 2 * u * t * control.y + t * t * to.y,
      size: from.size + (to.size - from.size) * t,
    }
    const offset = i === FLIGHT_STEPS ? 1 : FLIGHT_HOLD_OFFSET + (1 - FLIGHT_HOLD_OFFSET) * t
    frames.push(frame(offset, point, 1, baseSize))
  }

  return frames
}

/** Hareket azaltma: logo FAB'in yerinde solarak belirir. */
export function fadeKeyframes(to: FlightPoint, baseSize: number = FAB_SIZE_PX): FlightKeyframe[] {
  return [frame(0, to, 0, baseSize), frame(1, to, 1, baseSize)]
}
